import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MessageSquare, Send, Trash2, AtSign, MapPin, Loader2 } from 'lucide-react';
import { NoteService } from '../services/noteService';
import { AuditService, AUDIT_ACTIONS } from '../services/auditService';
import { NotifyService } from '../services/notifyService';
import { NotificationService } from '../services/notificationService';
import { convertFirestoreTimestamp } from '../utils/helpers';

const dayLabel = (d) => {
  if (!d) return "Aujourd'hui"; // horodatage serveur en attente = message qui vient d'être envoyé
  const date = new Date(d);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (date.toDateString() === yesterday.toDateString()) return 'Hier';
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

// Conversation d'un dossier : messages texte, @mentions (saisie « @ » avec autocomplétion clavier),
// partage de position, suppression de ses messages. Pas de pièces jointes (pas de stockage de fichiers).
const ProjectNotes = ({ project, author, agents = [], canDeleteAll = false }) => {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const mentionable = useMemo(() => Array.from(new Set([...agents, author?.name].filter(Boolean))), [agents, author?.name]);

  useEffect(() => {
    if (!project?.id) return undefined;
    return NoteService.subscribe(project.id, setNotes);
  }, [project?.id]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [notes.length]);

  const filteredMentions = mentionQuery !== null
    ? mentionable.filter((n) => n.toLowerCase().includes(mentionQuery)).slice(0, 6)
    : [];

  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);
    const before = value.slice(0, e.target.selectionStart);
    const m = before.match(/(?:^|\s)@([^\s@]*)$/);
    if (m && mentionable.length) { setMentionQuery(m[1].toLowerCase()); setMentionIndex(0); }
    else setMentionQuery(null);
  };

  const insertMention = (name) => {
    const el = inputRef.current;
    const pos = el ? el.selectionStart : text.length;
    const before = text.slice(0, pos);
    const after = text.slice(pos);
    const at = before.lastIndexOf('@');
    const mention = `@${name.replace(/\s+/g, '_')} `;
    const next = (at >= 0 ? before.slice(0, at) : before) + mention + after;
    setText(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      if (el) { el.focus(); const p = (at >= 0 ? at : before.length) + mention.length; el.setSelectionRange(p, p); }
    });
  };

  const send = async (content = text) => {
    if (!content.trim()) return;
    setSending(true);
    setError('');
    const r = await NoteService.add(project.id, content, author);
    setSending(false);
    if (!r.success) { setError(r.error); return; }
    const mentions = NoteService.extractMentions(content);
    AuditService.log(AUDIT_ACTIONS.NOTE_ADD, { entityType: 'dossier', entityId: project.id, label: `${project.refGlobal || ''} · ${content.trim().slice(0, 80)}` });
    NotificationService.message(project, content.trim());
    if (mentions.length) NotifyService.send('mention', { dossier: project.refGlobal, client: project.client, par: author.name, mentions, texte: content.trim() });
    setText('');
    setMentionQuery(null);
  };

  const handleKeyDown = (e) => {
    if (mentionQuery !== null && filteredMentions.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex((i) => (i + 1) % filteredMentions.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex((i) => (i - 1 + filteredMentions.length) % filteredMentions.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(filteredMentions[mentionIndex]); return; }
      if (e.key === 'Escape') { setMentionQuery(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const shareLocation = () => {
    if (!navigator.geolocation) { setError('Géolocalisation non disponible sur cet appareil'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const { latitude, longitude } = pos.coords;
        send(`📍 Ma position : https://maps.google.com/maps?q=${latitude.toFixed(6)},${longitude.toFixed(6)}`);
      },
      () => { setLocating(false); setError('Position introuvable (autorisez la géolocalisation)'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const remove = async (n) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    await NoteService.remove(n.id);
  };

  const renderText = (t) => t.split(/(@[\p{L}\p{N}_.-]+|https?:\/\/\S+)/gu).map((part, i) => {
    if (part.startsWith('@')) return <span key={i} className="font-semibold underline decoration-dotted">{part.replace(/_/g, ' ')}</span>;
    if (/^https?:\/\//.test(part)) return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline break-all">{part.includes('maps.google') ? 'Ouvrir la position sur Google Maps' : part}</a>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });

  // Regroupement par jour
  const groups = useMemo(() => {
    const out = [];
    notes.forEach((n) => {
      const d = convertFirestoreTimestamp(n.createdAt);
      const key = d ? new Date(d).toDateString() : new Date().toDateString();
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(n);
      else out.push({ key, label: dayLabel(d), items: [n] });
    });
    return out;
  }, [notes]);

  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide flex items-center">
        <MessageSquare className="h-4 w-4 mr-2" />
        Conversation <span className="ml-2 text-xs font-normal text-slate-400 normal-case">({notes.length} message{notes.length > 1 ? 's' : ''})</span>
      </h3>
      <div className="bg-slate-50 rounded-xl p-3 sm:p-4 space-y-3">
        <div ref={listRef} className="max-h-80 overflow-y-auto custom-scrollbar pr-1 space-y-3">
          {notes.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Aucun message. Écrivez un point d'avancement, tapez « @ » pour mentionner un agent, ou partagez votre position.</p>
          ) : groups.map((g) => (
            <div key={g.key} className="space-y-2">
              <div className="flex items-center gap-2 text-[11px] text-slate-400 uppercase tracking-wide"><span className="flex-1 h-px bg-slate-200" />{g.label}<span className="flex-1 h-px bg-slate-200" /></div>
              {g.items.map((n) => {
                const mine = n.authorUid === author?.uid;
                const d = convertFirestoreTimestamp(n.createdAt);
                return (
                  <div key={n.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${mine ? 'bg-brand-600 text-white rounded-br-md' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-md'}`}>
                      <div className={`flex items-center gap-2 text-[11px] mb-1 ${mine ? 'text-brand-100' : 'text-slate-500'}`}>
                        <span className="font-semibold">{mine ? 'Vous' : (n.authorName || 'Agent')}</span>
                        <span>{d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        {(mine || canDeleteAll) && (
                          <button onClick={() => remove(n)} className={`ml-auto p-1.5 rounded min-w-[28px] ${mine ? 'hover:bg-white/20' : 'hover:bg-slate-100'}`} aria-label="Supprimer le message"><Trash2 className="h-3 w-3" /></button>
                        )}
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-words">{renderText(n.text)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="relative">
          {mentionQuery !== null && filteredMentions.length > 0 && (
            <ul className="absolute bottom-full mb-1 left-0 bg-white border border-slate-200 rounded-xl shadow-card-hover p-1 z-10 w-60 max-h-48 overflow-y-auto" role="listbox">
              {filteredMentions.map((name, i) => (
                <li key={name}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === mentionIndex}
                    onMouseDown={(e) => { e.preventDefault(); insertMention(name); }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-lg min-h-[36px] ${i === mentionIndex ? 'bg-brand-600 text-white' : 'hover:bg-brand-50'}`}
                  >
                    @{name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-end gap-2">
            <button onClick={() => { setText((t) => `${t}${t && !t.endsWith(' ') ? ' ' : ''}@`); setMentionQuery(''); setMentionIndex(0); inputRef.current?.focus(); }} className="icon-btn flex-shrink-0" aria-label="Mentionner un agent" title="Mentionner"><AtSign className="h-4 w-4" /></button>
            <button onClick={shareLocation} disabled={locating} className="icon-btn flex-shrink-0" aria-label="Partager ma position" title="Partager ma position">
              {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            </button>
            <textarea
              ref={inputRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              rows={1}
              className="field flex-1 resize-none !min-h-[44px] max-h-32"
              placeholder="Message… (@ pour mentionner, Entrée pour envoyer)"
            />
            <button onClick={() => send()} disabled={sending || !text.trim()} className="btn-primary flex-shrink-0 !px-3" aria-label="Envoyer"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectNotes;
